import React, { useEffect, useMemo } from "react";
import Select, { components, ControlProps } from "react-select";
import CreatableSelect from "react-select/creatable";
import { useField } from "formik";
import { Img } from "components/Img";

interface DropdownProps {
  label?: string;
  required?: boolean;
  options: { value: string; label: string }[];
  name: string;
  placeHolder: string;
  isMulti?: boolean;
  labelClassNames?: string;
  customHeight?: string;
  leftIcon?: string;
  controlCustomStyle?: any;
  placeholderCustomStyle?: any;
  disable?: boolean;
  isCreatable?: boolean;
}

function hasMissingElements(options: any[], values: any[]) {
  return values?.some(
    (item) =>
      !options?.some(
        (entry) => entry.value.toLowerCase() === item.toLowerCase(),
      ),
  );
}

function addMissingElements(options: any[], values: any[]) {
  // FIX: Create a copy of the array so we don't accidentally mutate the original props!
  const updatedOptions = [...options];
  values.forEach((item) => {
    let isPresent = updatedOptions?.some(
      (entry) => entry.value.toLowerCase() === item.toLowerCase(),
    );

    if (!isPresent) {
      updatedOptions.push({ value: item, label: item });
    }
  });

  return updatedOptions;
}

// FIX: Move static components completely OUTSIDE the main function
const CustomDropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="9"
        height="5.4"
        viewBox="0 0 14 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: props.selectProps.menuIsOpen
            ? "rotate(180deg)"
            : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }}
      >
        <path
          d="M1 1L7 7L13 1"
          stroke="#000000"
          strokeWidth="1.62"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

const CustomIndicatorSeparator = () => null;

const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  name,
  placeHolder,
  required,
  isMulti = false,
  labelClassNames,
  customHeight = "",
  controlCustomStyle,
  placeholderCustomStyle,
  disable,
  isCreatable,
  leftIcon,
}) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const [customOption, setCustomOption] = React.useState<any[]>([]);

  const isError = meta.touched && meta.error;

  // FIX: Change useMemo to useEffect.
  // useMemo shouldn't be used to trigger side-effects like state setters.
  useEffect(() => {
    if (options?.length) {
      if (isMulti) {
        if (hasMissingElements(options, field.value) && !!field.value?.length) {
          setCustomOption(addMissingElements(options, field.value));
        } else {
          setCustomOption([...options]);
        }
      } else {
        if (
          !options.find((option) => option.value === field.value) &&
          !!field.value?.length
        ) {
          const newOption = { value: field.value, label: field.value };
          setCustomOption([...options, newOption]);
        } else {
          setCustomOption([...options]);
        }
      }
    }
  }, [options, field.value, isMulti]);

  const handleChange = (selectedOptions: any) => {
    if (isMulti) {
      const values = selectedOptions.map((option: any) => option.value);
      setValue(values);
    } else {
      const selectedValue = selectedOptions?.value || "";
      setValue(selectedValue);
    }
  };

  const handleCreate = (inputValue: string) => {
    const newOption = { value: inputValue, label: inputValue };
    setCustomOption((prevOption) => [...prevOption, newOption]);
    if (isMulti) {
      setValue([...(field.value || []), inputValue]);
    } else {
      setValue(inputValue);
    }
  };

  const getValue = () => {
    if (isMulti) {
      return customOption.filter((option) =>
        field?.value?.includes(option.value),
      );
    } else {
      return customOption.find((option) => option?.value === field?.value);
    }
  };

  // FIX: Memoize dynamic components to prevent remounting on every render
  const selectComponents = useMemo(
    () => ({
      Control: (props: ControlProps<any, boolean>) => (
        <components.Control
          {...props}
          innerRef={props.innerRef}
          innerProps={props.innerProps}
        >
          {!!leftIcon && (
            <div className="px-[7.2px]">
              <Img src={leftIcon} className="w-[18px] h-[18px]" />
            </div>
          )}
          {props.children}
        </components.Control>
      ),
      DropdownIndicator: CustomDropdownIndicator,
      IndicatorSeparator: CustomIndicatorSeparator,
    }),
    [leftIcon],
  );

  return (
    <div className="flex flex-col w-full gap-[0.198vw] relative pb-[2.2vh]">
      {!!label && (
        <label
          className={`font-inter text-[1.75vh] text-[#374151] font-[500] mb-[0.4vh] pl-[0.63vw] tracking-[0.01em] ${labelClassNames || ""}`}
        >
          {label} {!!required && <span style={{ color: '#E24B4A' }}>*</span>}
        </label>
      )}
      <div className="flex gap-[3.6px] w-full items-start">
        <div className="w-full ">
          {isCreatable ? (
            <CreatableSelect
              isDisabled={disable}
              menuPlacement="auto"
              options={customOption}
              isMulti={isMulti}
              closeMenuOnSelect={!isMulti}
              onChange={handleChange}
              onCreateOption={handleCreate}
              placeholder={placeHolder}
              menuPortalTarget={document.body}
              components={selectComponents} // <-- Using the memoized components here
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base, state) => ({
                  ...base,
                  borderRadius: "5.4px",
                  height: isMulti ? "auto" : "4.4vh",
                  minHeight: "4.4vh",
                  boxSizing: "border-box", // 1. ADD THIS: Forces border to be included in height
                  padding: "0",
                  margin: 0,
                  flexWrap: isMulti ? "wrap" : "nowrap",
                  border: isError ? "1px solid red" : "1px solid #9d9d9d",
                  boxShadow: "none",
                  borderColor: isError ? "red" : "#9d9d9d",
                  outline: state?.isFocused ? "2px solid #9B97F0" : "none",
                  outlineOffset: state?.isFocused ? "1px" : "auto",
                  ...(controlCustomStyle || {}),
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: "0 7.2px",
                  minHeight: "4.4vh", // 2. ADD THIS: Constrains the inner container
                  margin: 0,
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#e2e8f0", // Example: light gray background
                  borderRadius: "5.4px",
                  height: "2.7vh",


                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "#333", // Example: dark text
                  fontSize: "1.62vh",
                  fontFamily: "Inter, sans-serif",

                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }), // 3. ENSURE THIS IS IN BOTH: Removes default react-select input margins
                indicatorsContainer: (base) => ({
                  ...base,
                  height: "4.4vh", // 4. ADD THIS: Prevents the dropdown arrow container from stretching the box
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: "1.766vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  margin: 0,
                  opacity: 1,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#9CA3AF",
                  overflow: "hidden",
                  ...(placeholderCustomStyle || {}),
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: "1.766vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  color: "#000000",
                }),
                menu: (base) => ({
                  ...base,
                  maxHeight: "360px",
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  msOverflowStyle: "auto",
                  zIndex: 999,
                  background: "rgba(255, 255, 255, 0.8)",
                  border: "1.8px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "7.2px",
                  backdropFilter: "blur(18px)",
                  boxShadow: "0 3.6px 10.8px rgba(0, 0, 0, 0.1)",
                }),
                option: (base, { isFocused, isSelected }) => ({
                  ...base,
                  textAlign: "left",
                  fontSize: "1.766vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 300,
                  background: isSelected
                    ? "rgba(0, 123, 255, 0.8)"
                    : isFocused
                      ? "rgba(0, 123, 255, 0.2)"
                      : "transparent",
                  color: isSelected ? "#fff" : isFocused ? "#000" : "#333",
                  cursor: "pointer",
                  backdropFilter: isFocused ? "blur(4.5px)" : "none",
                  transition: "background 0.3s ease, color 0.3s ease",
                }),
              }}
              value={getValue()}
            />
          ) : (
            <Select
              isDisabled={disable}
              menuPlacement="auto"
              options={customOption}
              isMulti={isMulti}
              closeMenuOnSelect={!isMulti}
              onChange={handleChange}
              placeholder={placeHolder}
              components={selectComponents} // <-- Using the memoized components here
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base, state) => ({
                  ...base,
                  borderRadius: "5.4px",
                  // height: isMulti ? "auto" : "2.097vw",
                  minHeight: "4.4vh",
                  boxSizing: "border-box", // 1. ADD THIS: Forces border to be included in height
                  padding: "0",
                  margin: 0,
                  flexWrap: isMulti ? "wrap" : "nowrap",
                  border: isError ? "1px solid red" : "1px solid #9d9d9d",
                  boxShadow: "none",
                  borderColor: isError ? "red" : "#9d9d9d",
                  outline: state?.isFocused ? "2px solid #9B97F0" : "none",
                  outlineOffset: state?.isFocused ? "1px" : "auto",
                  ...(controlCustomStyle || {}),
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: "0 7.2px",
                  minHeight: "4.4vh", // 2. ADD THIS: Constrains the inner container
                  margin: 0,
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#e2e8f0", // Example: light gray background
                  borderRadius: "5.4px",
                  height: "2.7vh",

                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "#333", // Example: dark text
                  fontSize: "1.62vh",
                  fontFamily: "Inter, sans-serif",

                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }), // 3. ENSURE THIS IS IN BOTH: Removes default react-select input margins
                indicatorsContainer: (base) => ({
                  ...base,
                  height: "4.4vh", // 4. ADD THIS: Prevents the dropdown arrow container from stretching the box
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: "1.766vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  margin: 0,
                  opacity: 1,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#9CA3AF",
                  overflow: "hidden",
                  ...(placeholderCustomStyle || {}),
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: "1.766vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  color: "#000000",
                }),
                menu: (base) => ({
                  ...base,
                  maxHeight: "360px",
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  msOverflowStyle: "auto",
                  zIndex: 999,
                  background: "rgba(255, 255, 255, 0.8)",
                  border: "1.8px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "7.2px",
                  backdropFilter: "blur(18px)",
                  boxShadow: "0 3.6px 10.8px rgba(0, 0, 0, 0.1)",
                }),
                option: (base, { isFocused, isSelected }) => ({
                  ...base,
                  textAlign: "left",
                  fontSize: "1.766vh",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 300,
                  background: isSelected
                    ? "rgba(0, 123, 255, 0.8)"
                    : isFocused
                      ? "rgba(0, 123, 255, 0.2)"
                      : "transparent",
                  color: isSelected ? "#fff" : isFocused ? "#000" : "#333",
                  cursor: "pointer",
                  backdropFilter: isFocused ? "blur(4.5px)" : "none",
                  transition: "background 0.3s ease, color 0.3s ease",
                }),
              }}
              value={getValue()}
            />
          )}
        </div>
      </div>
      <div className="absolute bottom-0 left-[0.4vw] right-0 flex flex-col justify-start overflow-hidden">
        {isError && (
          <div className="text-[#E24B4A] text-[1.6vh] leading-none">
            {Array.isArray(meta.error) ? meta.error.join(", ") : meta.error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;